import { capitalize } from "lodash";
import Select from "react-select";
import { useEffect, useState } from "react";
import DateComponent from "./DateComponents";
import { daySelectOptions } from "../constant/attractor-select";
import { getTierOptions } from "../api/new-api";
import { useColors } from "../provider/colors-provider";
import moment from "moment";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";

const MobileSettingsContainer = ({
  data,
  openSettings,
  setOpenSettings,
  hideCloseButton,
  hideDeleteButton,
  submitText,
  handleSubmit,
  handleDelete,
  objectKey,
  showPromotionDisplayName = false,
  showTierRestrictions = false,
}: {
  data: any;
  openSettings: boolean;
  setOpenSettings: (arg: boolean) => void;
  hideCloseButton?: boolean;
  hideDeleteButton?: boolean;
  submitText?: string;
  handleSubmit: (arg: any) => void;
  handleDelete: (id: string | number) => void;
  objectKey: "mobileBannerId" | "mobileAttractorId";
  showPromotionDisplayName?: boolean;
  showTierRestrictions?: boolean;
}) => {
  const { primary } = useColors();

  const [isUrlValid, setIsUrlValid] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [newData, setNewData] = useState<any>(data);
  const [startDate, setStartDate] = useState<any>(data.startDateEpoch);
  const [endDate, setEndDate] = useState<any>(data.endDateEpoch);
  const [endDateManuallyChanged, setEndDateManuallyChanged] =
    useState<boolean>(false);
  const [selected, setSelected] = useState<any>(
    data.dayVisibility?.split(",").map((val: string | undefined) => ({
      value: val,
      label: capitalize(val),
    }))
  );

  const [tierOptions, setTierOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [selectedTierOption, setSelectedTierOption] = useState<any>({
    value: "No Tier",
    label: "No Tier",
  });
  const [showEveryday, setShowEveryday] = useState<any>(false);

  const handleFetchTiers = async () => {
    setShowEveryday(data.dayVisibility === "MON,TUE,WED,THU,FRI,SAT,SUN");
    if (!showTierRestrictions) return;

    const { tierOptions } = await getTierOptions();
    setTierOptions(
      tierOptions.map((val: string) => ({ value: val, label: val }))
    );

    const selectedTier = tierOptions?.find(
      (val: string) => val === data.tierLevelText
    );

    setSelectedTierOption({
      value: selectedTier ?? "No Tier",
      label: selectedTier ?? "No Tier",
    });

    setNewData((prevData: any) => ({
      ...prevData,
      hasSpecificUserTier:
        data.tierLevelText !== null && data.tierLevelText !== "" ? true : false,
    }));
  };

  const handleUpdate = () => {
    if (selected.length === 0) {
      alert("Please select at least one day");
      return;
    }
    if (!isUrlValid) {
      alert(
        "The URL is invalid. Please enter a valid URL (e.g., https://example.com) or leave it blank."
      );
      return;
    }
    let attractor: any = {
      startDateEpoch: new Date(startDate).getTime(),
      endDateEpoch: new Date(endDate).getTime(),
      dayVisibility: selected.map((val: any) => val.value).join(","),
      displayLinkUrl: newData.externalLinkUrl,
      ...{ [objectKey]: data[objectKey] },
    };

    if (showPromotionDisplayName) {
      attractor = {
        ...attractor,
        displayText: newData.displayText,
      };
    }

    if (showTierRestrictions && newData.hasSpecificUserTier) {
      attractor = {
        ...attractor,
        specificUserTier: selectedTierOption.value,
      };
    }

    handleSubmit(attractor);
    setOpenSettings(!openSettings);
  };

  useEffect(() => {
    handleFetchTiers();
  }, []);

  const isFullScreenType =
    data.externalLinkUrl === "" || data.externalLinkUrl === null ? true : false;

  return (
    <div>
      {showPromotionDisplayName && (
        <div className="my-4 flex flex-col">
          <p className="text-sm font-bold my-2">PROMOTION DISPLAY NAME</p>
          <input
            value={newData.displayText}
            onChange={(e) =>
              setNewData((prevData: any) => ({
                ...prevData,
                displayText: e.target.value,
              }))
            }
            type="text"
            className="border border-gray-200 rounded-md p-1 w-full"
            placeholder="Display text"
          />
        </div>
      )}
      {showTierRestrictions && (
        <div className="my-4 flex flex-col">
          <div className="flex items-center">
            <input
              checked={newData.hasSpecificUserTier}
              onChange={(e) =>
                setNewData((prevData: any) => ({
                  ...prevData,
                  hasSpecificUserTier: e.target.checked,
                }))
              }
              id={`checknbox-mobile-${data[objectKey]}-${data.sortKey}-tier`}
              type="checkbox"
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
            />
            <label
              htmlFor={`checknbox-mobile-${data[objectKey]}-${data.sortKey}-tier`}
              className="ms-2 text-md font-medium text-gray-900 dark:text-gray-300"
            >
              Enable Tier Restrictions
            </label>
          </div>
          {newData.hasSpecificUserTier && (
            <span className="mt-2">
              <Select
                value={selectedTierOption}
                options={tierOptions}
                onChange={(newValue) => setSelectedTierOption(newValue)}
              />
            </span>
          )}
        </div>
      )}
      {(data.bannerType === "WebsiteLink" || !isFullScreenType) &&
        data.attractorType !== "WithQueryParams" && (
          <div className="my-4 flex flex-col">
            <p className="text-sm font-bold my-2">LINK TO WEBSITE</p>
            <input
              value={newData.externalLinkUrl}
              onChange={(e) => {
                const newValue = e.target.value;
                const urlPattern = /^https?:\/\/.+?\..+?$/;
                const isValid = newValue === "" || urlPattern.test(newValue);
                setIsUrlValid(isValid);
                setNewData((prevData: any) => ({
                  ...prevData,
                  externalLinkUrl: e.target.value,
                }));
              }}
              type="text"
              className="border border-gray-200 rounded-md p-1 w-full"
              placeholder="Display text"
            />
            {!isUrlValid && (
              <p className="text-sm text-red-500 mt-1">
                URL must start with http:// or https:// and include a domain
                (e.g., example.com)
              </p>
            )}
          </div>
        )}

      <div className="my-2">
        <p className="text-sm font-bold my-2">START DATE:</p>
        <DateComponent
          value={startDate}
          setter={(date: any) => {
            // Check if start date is after end date
            if (endDate && date > endDate) {
              alert(
                "Start date should be before or equal to end date. Setting to 1 day before end date."
              );
              // Set start date to 1 day before end date
              const adjustedStartDate = moment(endDate)
                .subtract(1, "day")
                .toDate();
              setStartDate(adjustedStartDate);
              return;
            }
            setStartDate(date);
            // Only auto-set end date if it hasn't been manually changed
            if (!endDateManuallyChanged) {
              const minimumEndDate = moment(date).add(14, "days").toDate();
              setEndDate(minimumEndDate);
            }
          }}
        />
      </div>
      <div className="my-2">
        <p className="text-sm font-bold my-2">END DATE:</p>
        <DateComponent
          value={endDate}
          setter={(date: any) => {
            if (date <= startDate) {
              alert("End date should be greater than start date");

              const minimumEndDate = moment(startDate).add(14, "days").toDate();

              setEndDate(minimumEndDate);

              return;
            }
            setEndDate(date);
            setEndDateManuallyChanged(true);
          }}
        />
      </div>
      <div className="my-2 flex gap-4 items-center justify-between">
        <p className="text-sm font-bold">SHOWING DAYS:</p>
        <span className="flex items-center gap-2">
          <p className="text-sm font-bold">SHOW EVERYDAY:</p>
          <input
            checked={showEveryday}
            onChange={(e) => {
              if (e.target.checked) {
                setSelected(daySelectOptions);
                setShowEveryday(true);
              }
              if (!e.target.checked) {
                setSelected([]);
                setShowEveryday(false);
              }
            }}
            id="checked-checkbox"
            type="checkbox"
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
          />
        </span>
      </div>
      <div className="my-2">
        {/* <p className="text-sm font-bold my-2">CUSTOM DAYS:</p> */}
        <span>
          <Select
            isDisabled={showEveryday}
            isMulti
            value={selected}
            options={daySelectOptions}
            onChange={(newValue) => setSelected(newValue)}
          />
        </span>
      </div>
      <div className="my-2 flex flex-row gap-6 justify-between">
        <button
          onClick={handleUpdate}
          type="button"
          className="w-full rounded-md bg-primary-900 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
          style={{
            background: primary,
          }}
        >
          {submitText || "Update"}
        </button>

        {!hideCloseButton && (
          <button
            onClick={() => {
              setOpenSettings(!openSettings);
            }}
            type="button"
            className="w-full rounded-md bg-gray-50 px-2 py-1 text-sm font-semibold text-gray-600 shadow-sm hover:bg-gray-100"
          >
            Close
          </button>
        )}
        {!hideDeleteButton && (
          <button
            onClick={() => setDeleteDialogOpen(true)}
            type="button"
            className="w-full rounded-md bg-red-50 px-2 py-1 text-sm font-semibold text-red-600 shadow-sm hover:bg-red-100"
          >
            Delete
          </button>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {objectKey === "mobileBannerId"
            ? "Delete Mobile Banner"
            : "Delete Attractor"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this{" "}
            {objectKey === "mobileBannerId" ? "mobile banner" : "attractor"}?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleDelete(data[objectKey as keyof typeof data]);
              setDeleteDialogOpen(false);
            }}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MobileSettingsContainer;
